<template>
  <div v-if="ticket" class="sw-detail" :style="{ height: heightCss }">
    <!-- En-tête -->
    <div class="sw-d-head">
      <div class="sw-d-title">
        <div class="sw-d-societe" :title="ticket.societe">{{ ticket.societe || "Client non renseigné" }}</div>
        <div class="sw-d-sub">
          <span v-if="ticket.client">{{ ticket.client }} · </span>
          <span class="sw-d-id" :title="copiedId ? 'Copié !' : 'Copier le n° complet : ' + ticket.id" @click="copyId">#{{ shortId(ticket.id) }}</span>
          · créé {{ formatTs(ticket.creeMs) }}
          <template v-if="ticket.forme"> · <v-icon size="13">{{ formeIcon(ticket.forme) }}</v-icon> {{ ticket.forme }}</template>
        </div>
        <!-- Expéditeur de l'email (affiché seulement si le champ est rempli) -->
        <div v-if="ticket.mail_expediteur" class="sw-d-mail">
          <v-icon size="14" class="sw-d-mail-ic">mdi-email-outline</v-icon>
          <span v-if="ticket.mail_expediteur_nom" class="sw-d-mail-nom" :title="ticket.mail_expediteur_nom">{{ ticket.mail_expediteur_nom }}</span>
          <a :href="'mailto:' + mailAddr" class="sw-d-mail-adr" :title="'Écrire à ' + mailAddr">{{ ticket.mail_expediteur }}</a>
          <v-icon size="14" class="sw-copy" :title="copiedMail ? 'Copié !' : 'Copier l’adresse'" @click="copyMail">{{ copiedMail ? "mdi-check" : "mdi-content-copy" }}</v-icon>
        </div>
      </div>
      <v-btn icon variant="text" size="small" title="Fermer (Échap)" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Statut, prise en charge, infos client, actions -->
    <div class="sw-d-actions">
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button v-bind="props" type="button" class="sw-pill sw-d-statut" :style="{ backgroundColor: statutColor(ticket.statut) + '1F', color: statutColor(ticket.statut) }" :disabled="isSaving">
            <span class="sw-pill-dot"></span>{{ ticket.statut || "Sans statut" }}
            <v-icon size="15">mdi-chevron-down</v-icon>
          </button>
        </template>
        <v-list density="compact">
          <v-list-subheader>Changer le statut</v-list-subheader>
          <v-list-item v-for="s in statutOptions" :key="s" :disabled="s === ticket.statut" @click="$emit('changeStatut', s)">
            <template #prepend><span class="sw-dot" :style="{ backgroundColor: statutColor(s) }"></span></template>
            <v-list-item-title>{{ s }}</v-list-item-title>
            <template #append><v-icon v-if="s === ticket.statut" size="16" color="#4F5BD5">mdi-check</v-icon></template>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu v-model="assignMenu" location="bottom start" :close-on-content-click="false">
        <template #activator="{ props }">
          <button v-bind="props" type="button" class="sw-d-assign" :disabled="isSaving">
            <span class="sw-avatar sm" :class="{ me: isMeName(ticket.user_interne), empty: !ticket.user_interne }">
              {{ ticket.user_interne ? initials(ticket.user_interne) : "?" }}
            </span>
            <span class="sw-d-assign-txt">{{ ticket.user_interne || "Non assigné" }}</span>
            <v-icon size="15">mdi-chevron-down</v-icon>
          </button>
        </template>
        <v-card class="sw-assign-card">
          <div class="sw-assign-title">Pris en charge par</div>
          <v-list density="compact" class="sw-assign-list">
            <v-list-item v-for="u in userOptions" :key="u" :disabled="u === ticket.user_interne" @click="doAssign(u)">
              <template #prepend><span class="sw-avatar sm" :class="{ me: isMeName(u) }">{{ initials(u) }}</span></template>
              <v-list-item-title>{{ u }}<span v-if="isMeName(u)" class="sw-me-tag">moi</span></v-list-item-title>
            </v-list-item>
          </v-list>
          <div class="sw-assign-other">
            <v-text-field v-model="assignName" placeholder="Autre personne…" variant="outlined" density="compact" hide-details @keyup.enter="assignOther"></v-text-field>
            <v-btn variant="tonal" color="#4F5BD5" :disabled="!assignName.trim()" @click="assignOther">OK</v-btn>
          </div>
          <v-btn v-if="ticket.user_interne" block variant="text" color="#E5484D" class="sw-assign-clear" @click="doAssign('')">
            <v-icon start size="18">mdi-account-remove-outline</v-icon> Désassigner
          </v-btn>
        </v-card>
      </v-menu>

      <span v-if="client && client.repondre" class="sw-yn" :class="isYes(client.repondre) ? 'ok' : 'ko'">Tél. {{ yesNo(client.repondre) }}</span>
      <span v-if="client && client.heberge" class="sw-yn" :class="isYes(client.heberge) ? 'ok' : 'ko'">Hébergé {{ yesNo(client.heberge) }}</span>

      <span class="sw-d-actions-sp"></span>
      <button v-if="canTake" type="button" class="sw-d-btn" :disabled="isSaving" @click="$emit('take')">
        <v-icon size="16">mdi-account-check-outline</v-icon> Prendre en charge
      </button>
      <button v-if="!isClosed(ticket.statut)" type="button" class="sw-d-btn ok" :disabled="isSaving" @click="$emit('changeStatut', 'Résolu')">
        <v-icon size="16">mdi-check</v-icon> Résoudre
      </button>
      <button v-else type="button" class="sw-d-btn" :disabled="isSaving" @click="$emit('changeStatut', 'Ouvert')">
        <v-icon size="16">mdi-restore</v-icon> Rouvrir
      </button>
    </div>

    <!-- Résumé IA (champ ia_resume de WEB_SAV) : 3 lignes, clic pour tout voir s'il est long -->
    <div
      v-if="ticket.ia_resume"
      class="sw-d-ai"
      :class="{ long: aiLong, open: aiOpen }"
      :title="aiLong ? (aiOpen ? 'Réduire' : 'Afficher tout le résumé') : ''"
      @click="aiLong && (aiOpen = !aiOpen)"
    >
      <div class="sw-d-ai-head">
        <v-icon size="15">mdi-creation</v-icon> Résumé IA
        <v-icon v-if="aiLong" size="16" class="sw-d-ai-chev">{{ aiOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </div>
      <div class="sw-d-ai-text">{{ ticket.ia_resume }}</div>
    </div>

    <!-- Onglets -->
    <div class="sw-d-tabs">
      <button type="button" :class="{ on: tab === 'conv' }" @click="tab = 'conv'">Conversation <span class="sw-d-count">{{ conversation.length }}</span></button>
      <button type="button" :class="{ on: tab === 'client' }" @click="tab = 'client'">Fiche client</button>
      <button type="button" :class="{ on: tab === 'hist' }" @click="tab = 'hist'">Historique <span class="sw-d-count">{{ history.length }}</span></button>
      <v-progress-circular v-if="isEchangesLoading && echanges.length" indeterminate size="14" width="2" color="#4F5BD5" class="ml-auto"></v-progress-circular>
    </div>

    <!-- Corps défilant -->
    <div ref="body" class="sw-d-body">
      <!-- Conversation -->
      <template v-if="tab === 'conv'">
        <!-- Demande initiale = fil d'emails : un message par bulle, du plus ancien au plus récent -->
        <template v-if="mailThread.length">
          <div class="sw-ev-sys sw-thread-head">
            <v-icon size="14">{{ formeIcon(ticket.forme) }}</v-icon>
            <b>Demande initiale</b> · fil de {{ mailThread.length }} message{{ mailThread.length > 1 ? "s" : "" }}
            · <span :title="formatTs(ticket.creeMs)">{{ relTime(ticket.creeMs) }}</span>
          </div>
          <div
            v-for="(m, i) in mailThread"
            :key="'mail' + i"
            class="sw-ev sw-mail"
            :class="{ mine: m.internal, last: i === mailThread.length - 1 }"
          >
            <div class="sw-mail-head">
              <span class="sw-avatar sm sw-mail-av" :style="{ backgroundColor: mailColor(m) }">{{ mailInitials(m) }}</span>
              <span class="sw-mail-who">
                <span class="sw-mail-name" :title="m.email">{{ m.name || m.email || "Expéditeur inconnu" }}</span>
                <span v-if="m.name && m.email" class="sw-mail-addr">{{ m.email }}</span>
              </span>
              <span v-if="m.forwarded" class="sw-mail-tag">Transféré</span>
              <span v-if="m.date" class="sw-mail-date" :title="m.date">{{ m.dateLabel || m.date }}</span>
            </div>
            <div v-if="m.to || m.cc || m.subject" class="sw-mail-info">
              <span v-if="m.to" :title="m.to"><b>À</b> {{ m.to }}</span>
              <span v-if="m.cc" :title="m.cc"><b>Cc</b> {{ m.cc }}</span>
              <span v-if="m.subject" :title="m.subject"><b>Objet</b> {{ m.subject }}</span>
            </div>
            <div class="sw-ev-text">{{ m.body }}</div>
            <div v-if="m.signature" class="sw-mail-sign">{{ m.signature }}</div>
          </div>
        </template>
        <div v-else class="sw-ev">
          <div class="sw-ev-meta">
            <span class="sw-ev-type"><v-icon size="14">{{ formeIcon(ticket.forme) }}</v-icon>Demande initiale</span>
            <span class="sw-ev-time" :title="formatTs(ticket.creeMs)">{{ relTime(ticket.creeMs) }}</span>
          </div>
          <div class="sw-ev-text">{{ ticket.commentaire || "—" }}</div>
        </div>
        <div v-if="isEchangesLoading && !echanges.length" class="sw-d-loading">
          <v-progress-circular indeterminate size="26" width="3" color="#4F5BD5"></v-progress-circular>
        </div>
        <div v-else-if="!conversation.length" class="sw-d-empty">
          Aucun échange pour l'instant. Ajoutez une note ou tracez un appel ci-dessous.
        </div>
        <div v-for="e in conversation" :key="e.id" class="sw-ev" :class="{ mine: isMeName(e.auteur), pending: e.pending }">
          <div class="sw-ev-meta">
            <span class="sw-ev-type" :style="{ color: echangeMeta(e.type).color }">
              <v-icon size="14" :color="echangeMeta(e.type).color">{{ echangeMeta(e.type).icon }}</v-icon>
              {{ echangeMeta(e.type).label }}
            </span>
            <span class="sw-ev-author">{{ isMeName(e.auteur) ? "moi" : (e.auteur || "—") }}</span>
            <span class="sw-ev-time" :title="formatTs(e.creeMs)">{{ e.pending ? "envoi…" : relTime(e.creeMs) }}</span>
          </div>
          <div class="sw-ev-text">{{ e.message }}</div>
        </div>
      </template>

      <!-- Fiche client + modification du ticket -->
      <template v-else-if="tab === 'client'">
        <div class="sw-d-card">
          <div class="sw-d-grid">
            <div class="sw-d-field"><span class="sw-d-lab">Société</span><span class="sw-d-val">{{ ticket.societe || "—" }}</span></div>
            <div class="sw-d-field"><span class="sw-d-lab">Code client</span><span class="sw-d-val">{{ ticket.client || "—" }}</span></div>
            <div class="sw-d-field">
              <span class="sw-d-lab">Téléphone</span>
              <span v-if="phone" class="sw-d-val">
                <a :href="telHref(phone)" class="sw-tel">{{ phone }}</a>
                <v-icon size="15" class="sw-copy" :title="copied ? 'Copié !' : 'Copier'" @click="copyTel">{{ copied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
              </span>
              <span v-else class="sw-d-val muted">—</span>
            </div>
            <div class="sw-d-field"><span class="sw-d-lab">Forme</span><span class="sw-d-val">{{ ticket.forme || "—" }}</span></div>
            <div v-if="client && client.repondre" class="sw-d-field"><span class="sw-d-lab">Répondre au téléphone</span><span class="sw-yn" :class="isYes(client.repondre) ? 'ok' : 'ko'">{{ yesNo(client.repondre) }}</span></div>
            <div v-if="client && client.heberge" class="sw-d-field"><span class="sw-d-lab">Client hébergé</span><span class="sw-yn" :class="isYes(client.heberge) ? 'ok' : 'ko'">{{ yesNo(client.heberge) }}</span></div>
          </div>
          <div class="sw-d-cardfoot">
            <a v-if="phone" :href="telHref(phone)" class="sw-d-btn pri"><v-icon size="16">mdi-phone</v-icon> Appeler</a>
            <span class="sw-d-actions-sp"></span>
            <button v-if="!editMode" type="button" class="sw-d-btn" :disabled="isSaving" @click="startEdit">
              <v-icon size="16">mdi-pencil-outline</v-icon> Modifier le ticket
            </button>
          </div>
        </div>

        <!-- Mode édition : rien n'est enregistré avant « Enregistrer » -->
        <div v-if="editMode" class="sw-d-editbox">
          <div class="sw-grid2">
            <v-text-field v-model="draft.societe" label="Société" variant="outlined" density="compact" hide-details></v-text-field>
            <v-text-field v-model="draft.client" label="Code client" variant="outlined" density="compact" hide-details></v-text-field>
            <v-text-field v-model="draft.telephone" label="Téléphone" variant="outlined" density="compact" hide-details></v-text-field>
            <v-combobox v-model="draft.forme" :items="formeOptions" label="Forme" variant="outlined" density="compact" hide-details></v-combobox>
          </div>
          <v-textarea v-model="draft.commentaire" label="Demande initiale" variant="outlined" density="compact" hide-details auto-grow rows="3" max-rows="10" class="mt-2"></v-textarea>
          <div class="sw-d-editfoot">
            <v-spacer></v-spacer>
            <v-btn variant="text" color="#607D8B" @click="editMode = false">Annuler</v-btn>
            <v-btn variant="flat" color="#4F5BD5" style="color:white" :loading="isSaving" @click="saveEdit">
              <v-icon start>mdi-check</v-icon> Enregistrer
            </v-btn>
          </div>
        </div>
      </template>

      <!-- Historique : création + changements de statut / prise en charge / modifications -->
      <template v-else>
        <div class="sw-hist">
          <div v-for="h in history" :key="h.id" class="sw-hist-it">
            <span class="sw-hist-ic"><v-icon size="15">{{ h.icon }}</v-icon></span>
            <div class="sw-hist-txt">
              <div><b>{{ h.auteur || "—" }}</b> · {{ h.message }}</div>
              <div class="sw-ev-time" :title="formatTs(h.creeMs)">{{ formatTs(h.creeMs) }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Saisie d'un échange -->
    <div v-if="tab === 'conv'" class="sw-d-composer">
      <div class="sw-d-types">
        <button
          v-for="t in echangeTypes"
          :key="t.value"
          type="button"
          class="sw-type"
          :class="{ on: composeType === t.value }"
          :style="composeType === t.value ? { borderColor: t.color, color: t.color, backgroundColor: t.color + '14' } : {}"
          @click="composeType = t.value"
        >
          <v-icon size="15">{{ t.icon }}</v-icon>{{ t.label }}
        </button>
      </div>
      <div class="sw-d-row">
        <v-textarea
          v-model="composeText"
          :placeholder="composePlaceholder"
          variant="outlined"
          density="compact"
          hide-details
          auto-grow
          rows="2"
          max-rows="6"
          class="sw-d-input"
          @keydown="onComposerKey"
        ></v-textarea>
        <v-btn class="sw-send" variant="flat" color="#4F5BD5" :disabled="!composeText.trim()" :loading="isSending" @click="send">
          <v-icon start>mdi-send</v-icon> Envoyer
        </v-btn>
      </div>
      <div class="sw-d-hint">Ctrl + Entrée pour envoyer</div>
    </div>
  </div>
</template>

<script>
import { parseMailThread, mailColor } from "../core/mailThread";

const norm = (s) => (s === undefined || s === null ? "" : String(s)).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const PLACEHOLDERS = {
  note: "Ajouter une note interne…",
  appel_entrant: "Résumé de l'appel reçu…",
  appel_sortant: "Résumé de l'appel passé…",
  email: "Résumé de l'email…",
};

export default {
  props: {
    ticket: { type: Object, default: null },
    client: { type: Object, default: null },
    echanges: { type: Array, default: () => [] },
    isEchangesLoading: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
    isSending: { type: Boolean, default: false },
    statutOptions: { type: Array, default: () => [] },
    formeOptions: { type: Array, default: () => [] },
    userOptions: { type: Array, default: () => [] },
    currentUser: { type: String, default: "" },
    echangeTypes: { type: Array, default: () => [] },
    height: { type: [Number, String], default: 0 },
    statutColor: { type: Function, required: true },
    formatTs: { type: Function, required: true },
    relTime: { type: Function, required: true },
    echangeMeta: { type: Function, required: true },
    initials: { type: Function, required: true },
    telHref: { type: Function, required: true },
    formeIcon: { type: Function, required: true },
    isClosed: { type: Function, required: true },
    shortId: { type: Function, required: true },
  },
  emits: ["close", "changeStatut", "assign", "take", "saveFields", "addEchange"],
  data() {
    return {
      tab: "conv",
      composeType: "note",
      composeText: "",
      editMode: false,
      draft: { societe: "", client: "", telephone: "", forme: "", commentaire: "" },
      assignMenu: false,
      assignName: "",
      copied: false,
      copiedId: false,
      copiedMail: false,
      aiOpen: false,
    };
  },
  computed: {
    heightCss() {
      if (typeof this.height === "number") return this.height > 0 ? this.height + "px" : "86vh";
      return this.height || "86vh";
    },
    canTake() {
      return !!this.currentUser && norm(this.ticket.user_interne) !== norm(this.currentUser) && !this.isClosed(this.ticket.statut);
    },
    composePlaceholder() {
      return PLACEHOLDERS[this.composeType] || "Votre message…";
    },
    phone() {
      return this.ticket.telephone || (this.client && this.client.telephone) || "";
    },
    // Résumé IA trop long pour 3 lignes → repliable.
    aiLong() {
      const s = this.ticket.ia_resume || "";
      return s.length > 220 || s.split("\n").length > 3;
    },
    // Adresse seule (si le champ contient « Nom <adresse> »), pour le lien et la copie.
    mailAddr() {
      const v = this.ticket.mail_expediteur || "";
      const m = v.match(/[^\s<>"',;]+@[^\s<>"',;]+/);
      return m ? m[0] : v;
    },
    // Demande initiale contenant un fil d'emails (réponses, transferts) → messages
    // du plus ancien au plus récent. Vide si aucun message cité n'est détecté.
    mailThread() {
      const t = this.ticket;
      if (!t || !t.commentaire) return [];
      let sender = t.mail_expediteur_nom || "";
      if (t.mail_expediteur) sender = sender ? sender + " <" + this.mailAddr + ">" : t.mail_expediteur;
      const r = parseMailThread(t.commentaire, { sender: sender, date: this.formatTs(t.creeMs) });
      return r.hasHeaders ? r.messages : [];
    },
    // Messages (notes, appels, emails) d'un côté, événements système de l'autre.
    conversation() {
      return this.echanges.filter((e) => !this.echangeMeta(e.type).system);
    },
    history() {
      const created = {
        id: "creation", auteur: "", icon: "mdi-plus-circle-outline", creeMs: this.ticket.creeMs,
        message: "Ticket créé" + (this.ticket.forme ? " (" + this.ticket.forme + ")" : ""),
      };
      const events = this.echanges.filter((e) => this.echangeMeta(e.type).system)
        .map((e) => ({ id: e.id, auteur: e.auteur, icon: this.echangeMeta(e.type).icon, creeMs: e.creeMs, message: e.message }));
      return [created].concat(events).reverse(); // le plus récent en haut
    },
  },
  watch: {
    // Nouveau ticket affiché : on repart d'une saisie vierge, onglet Conversation.
    "ticket.id"() {
      this.aiOpen = false;
      this.tab = "conv";
      this.editMode = false;
      this.composeText = "";
      this.composeType = "note";
      this.assignMenu = false;
      this.scrollToBottom();
    },
    // Nouveau message : on descend si on était déjà en bas (ou si c'est le mien).
    "conversation.length"(n, o) {
      if (this.tab !== "conv" || n <= (o || 0)) return;
      const last = this.conversation[n - 1];
      if (!o || this.isNearBottom() || (last && this.isMeName(last.auteur))) this.scrollToBottom();
    },
    tab(t) {
      if (t === "conv") this.scrollToBottom();
    },
  },
  mounted() {
    this.scrollToBottom();
  },
  methods: {
    isMeName(name) {
      return !!name && norm(name) === norm(this.currentUser);
    },
    isYes(v) {
      return /^(oui|o|yes|y|1|vrai|true|x)$/.test(norm(v));
    },
    // Les champs FileMaker valent souvent 1/0 → affichage OUI/NON.
    yesNo(v) {
      return this.isYes(v) ? "OUI" : "NON";
    },
    send() {
      const text = this.composeText.trim();
      if (!text || this.isSending) return;
      this.$emit("addEchange", {
        type: this.composeType,
        message: text,
        done: (ok) => { if (ok) this.composeText = ""; },
      });
    },
    onComposerKey(e) {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.send();
      }
    },
    startEdit() {
      const t = this.ticket;
      this.draft = { societe: t.societe, client: t.client, telephone: t.telephone, forme: t.forme, commentaire: t.commentaire };
      this.editMode = true;
    },
    saveEdit() {
      this.$emit("saveFields", Object.assign({}, this.draft), (ok) => { if (ok) this.editMode = false; });
    },
    doAssign(name) {
      this.assignMenu = false;
      this.assignName = "";
      this.$emit("assign", name);
    },
    assignOther() {
      const n = this.assignName.trim();
      if (n) this.doAssign(n);
    },
    copyText(text, flag) {
      if (!text || !navigator.clipboard) return;
      navigator.clipboard.writeText(text).then(() => {
        this[flag] = true;
        setTimeout(() => { this[flag] = false; }, 1500);
      }).catch(() => {});
    },
    copyTel() {
      this.copyText(this.phone, "copied");
    },
    copyId() {
      this.copyText(this.ticket.id, "copiedId");
    },
    copyMail() {
      this.copyText(this.mailAddr, "copiedMail");
    },
    mailInitials(m) {
      return this.initials(m.name || (m.email || "").split("@")[0] || "?");
    },
    mailColor, // couleur d'avatar par correspondant (core/mailThread, partagée avec l'aperçu)
    isNearBottom() {
      const b = this.$refs.body;
      return !b || b.scrollHeight - b.scrollTop - b.clientHeight < 140;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const b = this.$refs.body;
        if (b) b.scrollTop = b.scrollHeight;
      });
    },
  },
};
</script>
